/**
 * <s-product-gallery> — main-image slider with thumbnails, arrows and a counter.
 *
 * Required markup:
 *   [data-gallery-track]            — horizontal scroll-snap track.
 *   [data-gallery-slide]            — each slide; data-media-id identifies its media.
 * Optional markup:
 *   [data-gallery-thumb]            — buttons with data-index; aria-current marks the active slide.
 *   [data-gallery-prev] / [data-gallery-next] — arrow buttons.
 *   [data-gallery-counter]          — text node updated to "n / total".
 *   data-variant-source="<selector>" — element whose descendant [data-variant-media-id]
 *                                     is re-rendered on variant change; the gallery
 *                                     scrolls to that media.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #variantObserver;
  #index = 0;
  #mediaId = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('click', this.#handleClick, opts);

    // Mouse wheel over the thumbnail strip scrolls it sideways so every thumbnail can be reached.
    const strip = this.querySelector('[data-gallery-thumbs]');
    strip?.addEventListener('wheel', (event) => {
      if (strip.scrollWidth <= strip.clientWidth) return;
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      const atStart = strip.scrollLeft <= 0 && delta < 0;
      const atEnd = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 1 && delta > 0;
      if (atStart || atEnd) return;
      event.preventDefault();
      strip.scrollLeft += delta;
    }, { ...opts, passive: false });

    // Hovering a thumbnail with the mouse shows that image in the main view.
    strip?.addEventListener('pointerover', (event) => {
      if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (thumb && Number(thumb.dataset.index) !== this.#index) this.goTo(Number(thumb.dataset.index));
    }, opts);

    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      // Viewport as root: phones see only the swiped-in slide, the desktop grid tracks the photo in view.
      root: null,
      threshold: 0.6,
    });
    this.slides.forEach((slide) => this.#observer.observe(slide));

    const sourceSelector = this.dataset.variantSource;
    const source = sourceSelector ? document.querySelector(sourceSelector) : null;
    if (source) {
      this.#mediaId = source.querySelector('[data-variant-media-id]')?.dataset.variantMediaId ?? null;
      this.#variantObserver = new MutationObserver(() => this.#syncToVariant(source));
      this.#variantObserver.observe(source, { childList: true, subtree: true });
    }

    this.#update(0);

    // Arriving from a collection card (?bild=<media id>): open on the photo the card showed, without a visible slide.
    const startMediaId = new URLSearchParams(window.location.search).get('bild');
    if (startMediaId) {
      const startIndex = this.slides.findIndex((slide) => slide.dataset.mediaId === startMediaId);
      if (startIndex > 0) {
        // Jump now and again once layout and images settle, so a late reflow cannot snap back to the first slide.
        const jump = () => this.goTo(startIndex, 'instant');
        jump();
        requestAnimationFrame(jump);
        window.addEventListener('load', jump, { once: true, signal: this.#ac.signal });
        return;
      }
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    this.#variantObserver?.disconnect();
  }

  get track() {
    return this.querySelector('[data-gallery-track]');
  }

  get slides() {
    return [...this.querySelectorAll('[data-gallery-slide]')];
  }

  goTo(index, behavior = 'smooth') {
    const slides = this.slides;
    if (!slides.length || !this.track) return;
    const clamped = Math.max(0, Math.min(index, slides.length - 1));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({
      left: slides[clamped].offsetLeft - this.track.offsetLeft,
      behavior: reduceMotion ? 'auto' : behavior,
    });
    this.#update(clamped);
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const thumb = event.target.closest('[data-gallery-thumb]');
    if (thumb) {
      this.goTo(Number(thumb.dataset.index));
      return;
    }
    if (event.target.closest('[data-gallery-prev]')) this.goTo(this.#index - 1);
    if (event.target.closest('[data-gallery-next]')) this.goTo(this.#index + 1);
  };

  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const index = this.slides.indexOf(entry.target);
      if (index !== -1) this.#update(index);
    }
  };

  #syncToVariant(source) {
    const mediaId = source.querySelector('[data-variant-media-id]')?.dataset.variantMediaId;
    // Only react to a real variant change, not to unrelated re-renders inside the form.
    if (!mediaId || mediaId === this.#mediaId) return;
    this.#mediaId = mediaId;
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
    if (index !== -1 && index !== this.#index) this.goTo(index);
  }

  #update(index) {
    this.#index = index;
    const total = this.slides.length;

    this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      const active = Number(thumb.dataset.index) === index;
      thumb.toggleAttribute('aria-current', active);
      if (active) thumb.setAttribute('aria-current', 'true');
      // Scroll only the thumbnail strip; scrollIntoView could move the whole page.
      const strip = thumb.closest('[data-gallery-thumbs]');
      if (active && strip) {
        strip.scrollTo({ left: thumb.offsetLeft - strip.offsetLeft - (strip.clientWidth - thumb.clientWidth) / 2 });
      }
    });

    const counter = this.querySelector('[data-gallery-counter]');
    if (counter) counter.textContent = `${index + 1} / ${total}`;

    // Mobile progress line inside the image: segment width 1/total, moved to the active slide.
    const progress = this.querySelector('[data-gallery-progress]');
    if (progress) {
      progress.style.setProperty('--n', String(Math.max(total, 1)));
      progress.style.setProperty('--i', String(index));
    }

    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= total - 1;
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
